import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { QuestionComponentProps } from '../../types';
import { CommentField } from '../CommentField';

export const TextQuestion: React.FC<QuestionComponentProps> = ({
  question,
  value,
  comment,
  language,
  onChange,
  onCommentChange,
  disabled = false,
  error,
  autoFocus = false,
}) => {
  const [localValue, setLocalValue] = useState<string>((value as string) || '');

  const getLocalizedText = (textRecord: Record<string, string>): string => {
    return textRecord[language] || textRecord['en'] || textRecord[Object.keys(textRecord)[0]] || '';
  };

  const getPlaceholderText = (): string => {
    return language === 'no' ? 'Skriv ditt svar her...' : 'Your answer here...';
  };

  // Get validation rules
  const maxLength = question.validationRules?.maxLength || 500;
  const minLength = question.validationRules?.minLength || 0;

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue((value as string) || '');
  }, [value]);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    // Enforce max length
    if (newValue.length <= maxLength) {
      setLocalValue(newValue);
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    // Final validation on blur
    if (localValue.length < minLength && question.isRequired) {
      // Handle validation error if needed
    }
  };

  const isOverLimit = localValue.length > maxLength;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 3 }}
    >
      <Typography variant="h5" sx={{ textAlign: 'center', mb: 2 }}>
        {getLocalizedText(question.title)}
      </Typography>

      {question.description && (
        <Typography sx={{ textAlign: 'center', mb: 4, maxWidth: 500, color: 'text.secondary' }}>
          {getLocalizedText(question.description)}
        </Typography>
      )}

      <Box sx={{ width: '100%', maxWidth: 500, position: 'relative' }}>
        <TextField
          multiline
          rows={4}
          fullWidth
          value={localValue}
          onChange={handleTextChange}
          onBlur={handleBlur}
          placeholder={getPlaceholderText()}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional: focus textarea for keyboard entry in survey flow
          autoFocus={autoFocus}
          error={!!error || isOverLimit}
          inputProps={{
            'aria-label': getLocalizedText(question.title),
            'aria-describedby': 'character-count',
            maxLength: maxLength + 10, // Allow slight overage for UX
          }}
          sx={{
            bgcolor: 'background.paper',
          }}
          data-testid="text-input"
        />

        <Typography
          id="character-count"
          variant="caption"
          sx={{
            position: 'absolute',
            right: 0,
            bottom: -24,
            color: isOverLimit ? 'error.main' : 'text.secondary',
          }}
          data-testid="character-count"
        >
          {localValue.length}/{maxLength}
        </Typography>
      </Box>

      {question.hasCommentField && onCommentChange && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <CommentField
            value={comment}
            onChange={onCommentChange}
            language={language}
            disabled={disabled}
            placeholder={
              question.commentFieldDescription
                ? getLocalizedText(question.commentFieldDescription)
                : undefined
            }
          />
        </Box>
      )}

      {error && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 2 }}
          role="alert"
          data-testid="error-message"
        >
          {error}
        </Typography>
      )}

      {isOverLimit && (
        <Typography
          color="error"
          variant="body2"
          sx={{ textAlign: 'center', mt: 1 }}
          role="alert"
          data-testid="length-error"
        >
          {language === 'no'
            ? `Teksten er ${localValue.length - maxLength} tegn for lang`
            : `Text is ${localValue.length - maxLength} characters too long`}
        </Typography>
      )}
    </Box>
  );
};
